import { useMemo, useState, type FC } from "react"
import { Link, useNavigate } from "react-router-dom"

import KnownIssueDaySummary from "./KnownIssueDaySummary"
import styles from "./KnownIssueListTable.module.scss"
import { type DaySummariesByIssueId } from "./day_summary"

import {
	IssueTypeBadge,
	LogLevelBadge,
	ModuleBadge,
	OpenSearchLink,
} from "@/souhl_admin/components/common_features/Badges"
import { FragmentType, getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

// TODO: ほとんどみてない

const KnownIssueListTableFragment = graphql(/* GraphQL */ `
	fragment KnownIssueListTable on KnownIssue {
		id
		description
		issueType
		logLevel
		logModuleName
		logKind
		logSalonIds
		status
		logKeyword
		logMatchMethod
		opensearchQueryUrl
		slackChannelNameToNotify
		mentionTargets {
			slackMentionTargetId
			name
		}
		thresholdCountToNotify
		cooldownHoursToNotify
		autoResolveDaysAfterLastActivity
		shouldSendIosCriticalNotification
		registerTime
		issueModifiedTime
	}
`)

type KnownIssueListTableProps = {
	knownIssues: FragmentType<typeof KnownIssueListTableFragment>[]
	/** Day summaries keyed by knownIssue.id. If not provided, loading or "-" is shown in the column */
	daySummariesByIssueId?: DaySummariesByIssueId
	/** Whether daySummaries are still loading */
	daySummariesLoading?: boolean
	selectedIds: Set<string>
	onSelectionChange: (ids: Set<string>) => void
	onResolve: (ids: string[]) => void
	onClose: (ids: string[]) => void
	onReopen: (ids: string[]) => void
	appliedStatusFilter: string | null | undefined // TODO: enum にしたい
	isActionLoading?: boolean
}

const getStatusLabel = (status: string): string => {
	switch (status) {
		case "OPEN":
			return "Open"
		case "MANUALLY_RESOLVED":
			return "Manually Resolved"
		case "AUTO_RESOLVED":
			return "Auto Resolved"
		case "CLOSED":
			return "Closed"
		default:
			return status
	}
}

const KnownIssueListTable: FC<KnownIssueListTableProps> = ({
	knownIssues,
	daySummariesByIssueId,
	daySummariesLoading = false,
	selectedIds,
	onSelectionChange,
	onResolve,
	onClose,
	onReopen,
	appliedStatusFilter,
	isActionLoading = false,
}) => {
	const navigate = useNavigate()
	const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
	const [showLast24HoursOnly, setShowLast24HoursOnly] = useState(false)

	const issuesData = knownIssues.map((issue) =>
		getFragmentData(KnownIssueListTableFragment, issue)
	)

	// Filter issues by last 24 hours activity
	const filteredIssues = useMemo(() => {
		if (!showLast24HoursOnly) return issuesData

		return issuesData.filter((issue) => {
			const entry = daySummariesByIssueId?.get(issue.id)
			if (!entry) return false
			return entry.last24HoursMatchCount > 0
		})
	}, [issuesData, showLast24HoursOnly, daySummariesByIssueId])

	// Determine which status can be selected based on current filter
	const isResolvedFilter =
		appliedStatusFilter === "MANUALLY_RESOLVED" ||
		appliedStatusFilter === "AUTOMATICALLY_RESOLVED"

	// Selectable issues depend on current filter
	const selectableIssues = issuesData.filter((issue) => {
		if (isResolvedFilter) {
			// When filtering by resolved status, only resolved issues can be selected
			return (
				issue.status === "MANUALLY_RESOLVED" ||
				issue.status === "AUTOMATICALLY_RESOLVED"
			)
		}
		// Default: only OPEN issues can be selected for bulk resolve/close
		return issue.status === "OPEN"
	})
	const allSelectableSelected =
		selectableIssues.length > 0 &&
		selectableIssues.every((issue) => selectedIds.has(issue.id))

	const handleCreateClick = () => {
		navigate("/souhl_admin/known_issues/create")
	}

	const handleSelectAll = () => {
		if (allSelectableSelected) {
			onSelectionChange(new Set())
		} else {
			onSelectionChange(new Set(selectableIssues.map((issue) => issue.id)))
		}
	}

	const handleSelectOne = (id: string) => {
		const newSelected = new Set(selectedIds)
		if (newSelected.has(id)) {
			newSelected.delete(id)
		} else {
			newSelected.add(id)
		}
		onSelectionChange(newSelected)
	}

	const handleResolve = () => {
		if (selectedIds.size > 0) {
			onResolve(Array.from(selectedIds))
		}
	}

	const handleClose = () => {
		if (selectedIds.size > 0) {
			onClose(Array.from(selectedIds))
		}
	}

	const handleReopen = () => {
		if (selectedIds.size > 0) {
			onReopen(Array.from(selectedIds))
		}
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>Known Issues 管理</h1>
				<div className={styles.filterSection}>
					<label className={styles.filterLabel}>
						<input
							type="checkbox"
							checked={showLast24HoursOnly}
							onChange={(e) => setShowLast24HoursOnly(e.target.checked)}
							disabled={daySummariesLoading}
						/>
						24時間以内発生のみ
					</label>
				</div>
				<div className={styles.bulkActions}>
					{selectedIds.size > 0 && (
						<>
							<span className={styles.selectedCount}>
								{selectedIds.size} 件選択中
							</span>
							{isResolvedFilter ? (
								<button
									className={styles.reopenButton}
									onClick={handleReopen}
									disabled={isActionLoading}
								>
									Open に戻す
								</button>
							) : (
								<>
									<button
										className={styles.resolveButton}
										onClick={handleResolve}
										disabled={isActionLoading}
									>
										Resolve
									</button>
									<button
										className={styles.closeButton}
										onClick={handleClose}
										disabled={isActionLoading}
									>
										Close
									</button>
								</>
							)}
						</>
					)}
					<button className={styles.createButton} onClick={handleCreateClick}>
						+ 新規作成
					</button>
				</div>
			</div>

			<div className={styles.tableWrapper}>
				<h2 className={styles.tableTitle}>Known Issues 一覧</h2>
				<table className={styles.table}>
					<thead>
						<tr>
							<th className={`${styles.headerCell} ${styles.checkboxCell}`}>
								<input
									type="checkbox"
									className={styles.checkbox}
									checked={allSelectableSelected}
									onChange={handleSelectAll}
									disabled={selectableIssues.length === 0}
								/>
							</th>
							<th className={styles.headerCell}>Description</th>
							<th className={styles.headerCell}>Level</th>
							<th className={styles.headerCell}>Module</th>
							<th className={styles.headerCell}>Type</th>
							<th className={styles.headerCell}>Kind</th>
							<th className={styles.headerCell}>Keyword</th>
							<th className={styles.headerCell}>Salon</th>
							<th className={styles.headerCell}>iOS</th>
							<th className={styles.headerCell}>7日間</th>
							<th className={styles.headerCell}>OS</th>
							<th className={styles.headerCell}>操作</th>
						</tr>
					</thead>
					<tbody>
						{filteredIssues.length === 0 ? (
							<tr>
								<td colSpan={12} className={styles.emptyCell}>
									{showLast24HoursOnly
										? "24時間以内に発生した Known Issue はありません"
										: "データがありません"}
								</td>
							</tr>
						) : (
							filteredIssues.map((issue) => {
								const isSelectable = isResolvedFilter
									? issue.status === "MANUALLY_RESOLVED" ||
										issue.status === "AUTOMATICALLY_RESOLVED"
									: issue.status === "OPEN"
								const isHovered = hoveredRowId === issue.id
								return (
									<tr
										key={issue.id}
										className={styles.row}
										onMouseEnter={() => setHoveredRowId(issue.id)}
										onMouseLeave={() => setHoveredRowId(null)}
									>
										<td className={`${styles.cell} ${styles.checkboxCell}`}>
											{isSelectable && (
												<input
													type="checkbox"
													className={styles.checkbox}
													checked={selectedIds.has(issue.id)}
													onChange={() => handleSelectOne(issue.id)}
												/>
											)}
										</td>
										<td className={styles.cell}>
											<div className={styles.descriptionWrapper}>
												<span
													className={styles.descriptionCell}
													title={issue.description ?? ""}
												>
													{issue.description && issue.description.length > 30
														? `${issue.description.slice(0, 30)}...`
														: issue.description || "-"}
												</span>
												{issue.description &&
													issue.description.length > 30 &&
													isHovered && (
														<div className={styles.descriptionTooltip}>
															{issue.description}
														</div>
													)}
											</div>
										</td>
										<td className={styles.cell}>
											<LogLevelBadge level={issue.logLevel} />
										</td>
										<td className={styles.cell}>
											<ModuleBadge moduleName={issue.logModuleName} />
										</td>
										<td className={styles.cell}>
											<IssueTypeBadge issueType={issue.issueType} />
										</td>
										<td className={styles.cell}>
											<span className={styles.kindCell}>
												{issue.logKind || "-"}
											</span>
										</td>
										<td className={styles.cell}>
											<span
												className={styles.keywordCell}
												title={issue.logKeyword}
											>
												{issue.logKeyword.length > 25
													? `${issue.logKeyword.slice(0, 25)}...`
													: issue.logKeyword}
											</span>
										</td>
										<td className={styles.cell}>
											<span className={styles.salonCell}>
												{issue.logSalonIds.length > 0
													? issue.logSalonIds.join(", ")
													: "-"}
											</span>
										</td>
										<td className={styles.cell}>
											<input
												type="checkbox"
												checked={issue.shouldSendIosCriticalNotification}
												disabled
												className={styles.iosCheckbox}
											/>
										</td>
										<td className={styles.cell}>
											{daySummariesLoading ? (
												<span className={styles.loading}>読み込み中...</span>
											) : daySummariesByIssueId?.get(issue.id) ? (
												<KnownIssueDaySummary
													daySummaries={
														daySummariesByIssueId.get(issue.id)!.daySummaries
													}
													compact
												/>
											) : (
												<span>-</span>
											)}
										</td>
										<td className={styles.cell}>
											<OpenSearchLink url={issue.opensearchQueryUrl} />
										</td>
										<td className={styles.cell}>
											<div className={styles.actionLinks}>
												<Link
													to={`/souhl_admin/known_issues/${issue.id}/edit`}
													className={styles.link}
												>
													編集
												</Link>
												<Link
													to={`/souhl_admin/known_issues/${issue.id}`}
													className={styles.link}
												>
													詳細
												</Link>
											</div>
										</td>

										{/* Row tooltip */}
										{isHovered && (
											<td className={styles.rowTooltipContainer}>
												<div className={styles.rowTooltip}>
													<div className={styles.tooltipSection}>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Description:
															</span>
															<span>{issue.description || "-"}</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Status:
															</span>
															<span>{getStatusLabel(issue.status)}</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Keyword:
															</span>
															<code className={styles.tooltipCode}>
																{issue.logKeyword}
															</code>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Match:
															</span>
															<span>{issue.logMatchMethod}</span>
														</div>
													</div>
													<div className={styles.tooltipSection}>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Slack:
															</span>
															<span>#{issue.slackChannelNameToNotify}</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Mention:
															</span>
															<span>
																{issue.mentionTargets.length > 0
																	? issue.mentionTargets
																			.map((t) => t.name)
																			.join(", ")
																	: "-"}
															</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Threshold:
															</span>
															<span>{issue.thresholdCountToNotify}回</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Cooldown:
															</span>
															<span>
																{issue.cooldownHoursToNotify != null
																	? `${issue.cooldownHoursToNotify}h`
																	: "なし"}
															</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																Auto Resolve:
															</span>
															<span>
																{issue.autoResolveDaysAfterLastActivity}日
															</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>
																iOS通知:
															</span>
															<span>
																{issue.shouldSendIosCriticalNotification
																	? "有効"
																	: "無効"}
															</span>
														</div>
													</div>
													<div className={styles.tooltipSection}>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>登録:</span>
															<span>
																{new Date(issue.registerTime).toLocaleString(
																	"ja-JP"
																)}
															</span>
														</div>
														<div className={styles.tooltipRow}>
															<span className={styles.tooltipLabel}>更新:</span>
															<span>
																{new Date(
																	issue.issueModifiedTime
																).toLocaleString("ja-JP")}
															</span>
														</div>
													</div>
												</div>
											</td>
										)}
									</tr>
								)
							})
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default KnownIssueListTable
